import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import {
  Ticket as TicketIcon, Send, ArrowRight, Check, X, Loader2, Clock,
  Inbox, Users, Building2, AlertCircle, CheckCircle2, Trash2, FolderPlus, ImagePlus, FileText, Download, CheckCheck,
} from 'lucide-react'
import { prepareAttachment, DOC_EXTENSIONS, type WebpResult } from '@/lib/toWebp'
import { useSession } from '@/context/AuthContext'
import { useToast } from '@/context/ToastContext'
import { VisibilityPicker, VisibilityBadge } from '@/components/VisibilityPicker'
import { visibilityNeedsViewers, canSeeAllInDepartment, type Visibility } from '@/lib/roles'
import { formatDate } from '@/lib/utils'
import { deptColor } from '@/lib/departments'
import { PeoplePicker } from '@/components/PeoplePicker'
import { useRegisteredUsers } from '@/lib/useRegisteredUsers'

// ─── Tipe ──────────────────────────────────────────────────────────────────
interface TicketRecipient {
  email: string
  name: string | null
  department: string | null
  response: 'accepted' | 'rejected' | null
  responseNote: string | null
  respondedAt: string | null
}
interface Ticket {
  id: string
  ticketNo: number | null
  ticketKey: string
  recipients: TicketRecipient[]
  title: string
  description: string
  requesterEmail: string
  requesterName: string | null
  requesterDepartment: string
  status: 'pending' | 'in_progress' | 'done' | 'rejected'
  priority: 'low' | 'medium' | 'high' | null
  assigneeEmail: string | null
  assigneeName: string | null
  dueDate: string | null
  decidedByEmail: string | null
  decidedByName: string | null
  decidedAt: string | null
  decisionNote: string | null
  /** Project yang lahir dari tiket ini (null selama belum dijadikan project). */
  projectId: string | null
  visibility: Visibility
  /** Nomor antrean kerja global (hanya untuk tiket 'in_progress'). */
  queuePosition: number | null
  queueTotal: number
  createdAt: string
  updatedAt: string
}
type Priority = 'urgent' | 'high' | 'medium' | 'low'
export interface TicketEvent {
  id: string
  kind: string
  actorEmail: string | null
  actorName: string | null
  note: string | null
  createdAt: string
}
interface WorkloadMember { email: string; name: string; openLoad: number }
interface Payload {
  holding: boolean
  tickets: Ticket[]
  summary?: { pendingTotal: number; perBU: Record<string, number>; workload: WorkloadMember[] }
}

// Klasifikasi prioritas. Definisinya ikut ditampilkan di UI supaya orang menilai
// dengan ukuran yang sama; tanpa itu "High" cuma berarti "menurut saya penting".
const PRIORITIES: Priority[] = ['urgent', 'high', 'medium', 'low']
const PRIO: Record<string, { label: string; cls: string; guide: string }> = {
  urgent: { label: 'Urgent', cls: 'text-[#7F1D1D] bg-[#FEE2E2] border-[#FCA5A5]',
            guide: 'Menghambat operasional hari ini. Dikerjakan mendahului yang lain.' },
  high:   { label: 'High',   cls: 'text-[#DC2626] bg-[#FEF2F2] border-[#FECACA]',
            guide: 'Ada tenggat dekat atau menahan pekerjaan orang lain.' },
  medium: { label: 'Medium', cls: 'text-[#B45309] bg-[#FEF3C7] border-[#FDE68A]',
            guide: 'Perlu dikerjakan, tapi tidak menahan siapa pun. Default.' },
  low:    { label: 'Low',    cls: 'text-[#059669] bg-[#F0FDF4] border-[#BBF7D0]',
            guide: 'Bisa menunggu giliran. Tidak ada tenggat.' },
}
const ST: Record<Ticket['status'], { label: string; cls: string }> = {
  pending:     { label: 'Menunggu',   cls: 'bg-[#FEF3C7] text-[#B45309]' },
  in_progress: { label: 'Dikerjakan', cls: 'bg-[#DBEAFE] text-[#1D4ED8]' },
  done:        { label: 'Selesai',    cls: 'bg-[#DCFCE7] text-[#16A34A]' },
  rejected:    { label: 'Ditolak',    cls: 'bg-[#FEE2E2] text-[#DC2626]' },
}
const PW: Record<string, number> = { urgent: 4, high: 3, medium: 2, low: 1 }

/** Batas lampiran per tiket — disamakan dengan server (server/src/lib/attachments.ts). */
const MAX_LAMPIRAN = 5

const formatKB = (b: number) => `${Math.max(1, Math.round(b / 1024))} KB`

function StatusBadge({ s }: { s: Ticket['status'] }) {
  const c = ST[s]
  return <span className={`text-[10px] px-2 py-0.5 rounded-full font-body font-semibold ${c.cls}`}>{c.label}</span>
}
// Penanda tiket terbatas (bukan 'public').
const ConfidentialBadge = VisibilityBadge
function PrioBadge({ p }: { p: string | null }) {
  if (!p || !PRIO[p]) return null
  const c = PRIO[p]
  return <span className={`text-[10px] px-2 py-0.5 rounded-full font-body font-bold border ${c.cls}`}>{c.label}</span>
}

/**
 * Transparansi prioritas untuk pengaju.
 * Prioritas hanya punya SATU sumber: orang yang menilai tiketnya. Tebakan mesin
 * dicabut karena terbaca seperti keputusan, padahal belum dinilai siapa pun.
 */
function PriorityInfo({ t }: { t: Ticket }) {
  const value = t.priority
  if (!value) {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full font-body font-semibold bg-[#F4F3F0] text-[#8A8A85] border border-[#E8E7E3]">
        <Clock className="w-2.5 h-2.5" /> Prioritas belum dinilai
      </span>
    )
  }
  return (
    <span className="inline-flex items-center gap-1">
      <PrioBadge p={value} />
    </span>
  )
}

/** Pilih prioritas. Field manual: siapa pun yang terlibat boleh mengoreksinya. */
function PriorityPicker({ t, busy, onSet }: { t: Ticket; busy: boolean; onSet: (p: Priority) => void }) {
  return (
    <div className="flex items-center gap-1 flex-wrap" role="group" aria-label={`Prioritas ${t.title}`}>
      {PRIORITIES.map((p) => {
        const chosen = t.priority === p
        return (
          <button key={p} onClick={() => onSet(p)} disabled={busy}
            aria-pressed={chosen}
            title={`${PRIO[p].label} — ${PRIO[p].guide}`}
            className={`px-2.5 py-1 rounded-lg text-[11px] font-body font-bold border transition-all min-h-[32px] disabled:opacity-50 ${
              chosen ? `${PRIO[p].cls} ring-1 ring-current`
                : 'bg-white text-[#8A8A85] border-[#E8E7E3] hover:border-[#C8C7BF]'
            }`}>
            {PRIO[p].label}
          </button>
        )
      })}
    </div>
  )
}

/** Keterangan tiap tingkat prioritas — dipasang di form kirim & detail tiket. */
function PriorityGuide() {
  return (
    <ul className="mt-2 space-y-1">
      {PRIORITIES.map((p) => (
        <li key={p} className="flex items-start gap-2 text-[11px] font-body text-[#8A8A85]">
          <span className={`px-1.5 py-0.5 rounded font-bold border flex-shrink-0 ${PRIO[p].cls}`}>{PRIO[p].label}</span>
          <span className="leading-relaxed">{PRIO[p].guide}</span>
        </li>
      ))}
    </ul>
  )
}

// ─── Halaman ────────────────────────────────────────────────────────────────
export default function Ticketing() {
  const { data: session } = useSession()
  const { toast } = useToast()
  const dept = session?.user?.department ?? null
  const holding = dept === 'HOLDING'

  const [data, setData] = useState<Payload>({ holding: false, tickets: [] })
  const [loading, setLoading] = useState(true)
  const [view, setView] = useState<'tickets' | 'inbox' | 'board' | 'history'>('tickets')
  // Satu tiket yang sedang dibuka. Dipegang di level halaman supaya kartu di papan
  // mana pun (kotak masuk, kanban, riwayat) membuka detail yang sama.
  const [detailId, setDetailId] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      const res = await fetch('/api/ticketing')
      const d = await res.json()
      setData(d && Array.isArray(d.tickets) ? d : { holding, tickets: [] })
    } catch { /* abaikan */ } finally { setLoading(false) }
  }, [holding])
  useEffect(() => { load() }, [load])

  return (
    <div className="max-w-6xl mx-auto px-5 py-6">
      <header className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-[#1A1916] text-white flex items-center justify-center flex-shrink-0">
          <TicketIcon className="w-5 h-5" />
        </div>
        <div>
          <h1 className="font-display text-2xl font-bold text-[#1A1916] leading-tight">Ticketing</h1>
          <p className="text-[13px] text-[#8A8A85] font-body">
            {holding ? 'Antrean request masuk dari unit bisnis — ACC, tolak, & delegasikan.' : 'Ajukan tiket & pantau statusnya.'}
          </p>
        </div>
      </header>

      {/* Tab: sistem ticketing baru vs penugasan project (pindahan bell lama) */}
      <div className="flex gap-1 mb-5 border-b border-[#EEEDE8]">
        {([
          ['tickets', 'Ticketing'], ['inbox', 'Untuk Saya'], ['board', 'Progress'],
          ['history', 'Riwayat'],
        ] as const).map(([k, l]) => (
          <button key={k} onClick={() => setView(k)}
            className={`px-4 py-2 text-[13px] font-body font-medium border-b-2 -mb-px transition-colors ${view === k ? 'border-[#1A1916] text-[#1A1916]' : 'border-transparent text-[#8A8A85] hover:text-[#1A1916]'}`}>
            {l}
          </button>
        ))}
      </div>

      {view === 'inbox' ? (
        <InboxView tickets={data.tickets} myEmail={session?.user?.email ?? null} reload={load} toast={toast}
          onOpen={setDetailId} />
      ) : view === 'board' ? (
        <BoardView tickets={data.tickets} myEmail={session?.user?.email ?? null} reload={load} toast={toast}
          onOpen={setDetailId} />
      ) : view === 'history' ? (
        <HistoryView tickets={data.tickets} myEmail={session?.user?.email ?? null} onOpen={setDetailId} />
      ) : loading ? (
        <div className="flex items-center gap-2 text-[#8A8A85] font-body text-sm py-16 justify-center">
          <Loader2 className="w-4 h-4 animate-spin" /> Memuat…
        </div>
      ) : holding ? (
        <HoldingView data={data} reload={load} me={session?.user?.email ?? ''} toast={toast} onOpen={setDetailId} />
      ) : (
        <RequesterView tickets={data.tickets} reload={load} toast={toast} myEmail={session?.user?.email ?? null}
          onOpen={setDetailId} />
      )}

      {detailId && (
        <TicketDetailModal id={detailId} myEmail={session?.user?.email ?? null}
          onClose={() => setDetailId(null)} reload={load} toast={toast} />
      )}
    </div>
  )
}

function RequesterView({ tickets, reload, toast, myEmail, onOpen }: {
  tickets: Ticket[]; reload: () => void; toast: (m: string, t?: 'success' | 'error') => void
  myEmail: string | null; onOpen: (id: string) => void
}) {
  const [title, setTitle] = useState('')
  const [desc, setDesc] = useState('')
  const [busy, setBusy] = useState(false)
  // Default terbuka se-BU; non-Member boleh mempersempit lewat picker.
  const [visibility, setVisibility] = useState<Visibility>('public')
  const [viewerEmails, setViewerEmails] = useState<string[]>([])
  // Penerima dipilih langsung — inti alur baru: tiket mendarat di orangnya, bukan antrean.
  const [recipientEmails, setRecipientEmails] = useState<string[]>([])
  // Prioritas dinilai manusia sejak awal. Ini bacaan PENGAJU; penerima boleh
  // mengoreksinya nanti lewat detail tiket, dan perubahannya tercatat.
  const [priority, setPriority] = useState<Priority>('medium')
  const [showGuide, setShowGuide] = useState(false)
  // Lampiran dikonversi ke WebP saat DIPILIH, bukan saat dikirim, supaya pengguna
  // langsung melihat hasil dan ukurannya, dan tidak menunggu lama di tombol kirim.
  const [lampiran, setLampiran] = useState<WebpResult[]>([])
  const [mengonversi, setMengonversi] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)

  const pilihGambar = async (files: FileList | null) => {
    if (!files?.length) return
    const muat = MAX_LAMPIRAN - lampiran.length
    if (muat <= 0) { toast(`Maksimal ${MAX_LAMPIRAN} lampiran`, 'error'); return }
    setMengonversi(true)
    try {
      const hasil: WebpResult[] = []
      for (const f of Array.from(files).slice(0, muat)) {
        try { hasil.push(await prepareAttachment(f)) }
        catch (e) { toast(e instanceof Error ? e.message : `Gagal memproses ${f.name}`, 'error') }
      }
      setLampiran((prev) => [...prev, ...hasil])
    } finally {
      setMengonversi(false)
      if (fileRef.current) fileRef.current.value = ''
    }
  }

  const buangLampiran = (i: number) => {
    setLampiran((prev) => {
      const l = prev[i]
      if (l?.previewUrl) URL.revokeObjectURL(l.previewUrl)
      return prev.filter((_, n) => n !== i)
    })
  }

  const submit = async () => {
    if (!title.trim()) { toast('Judul request wajib diisi', 'error'); return }
    if (recipientEmails.length === 0) { toast('Pilih minimal satu penerima', 'error'); return }
    if (visibilityNeedsViewers(visibility) && !viewerEmails.length) {
      toast('Pilih minimal satu orang yang boleh melihat', 'error'); return
    }
    setBusy(true)
    try {
      const res = await fetch('/api/ticketing', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: title.trim(), description: desc.trim(), visibility, viewerEmails, recipientEmails, priority }),
      })
      const d = await res.json()
      if (!res.ok) { toast(d.error ?? 'Gagal mengirim', 'error'); return }

      // Lampiran diunggah SETELAH tiketnya ada, karena objeknya disimpan per tiket.
      // Kegagalan di sini tidak membatalkan tiketnya: tiket yang sudah terkirim jauh
      // lebih berharga daripada gambarnya, dan pengguna diberi tahu apa yang kurang.
      if (lampiran.length > 0 && d.id) {
        const form = new FormData()
        for (const l of lampiran) form.append('file', l.file)
        const up = await fetch(`/api/ticketing/${d.id}/attachments`, { method: 'POST', body: form })
        if (!up.ok) {
          const ud = await up.json().catch(() => null)
          toast(ud?.error ?? 'Tiket terkirim, tapi lampirannya gagal diunggah.', 'error')
        }
      }

      toast(`Tiket ${d.ticketKey ?? ''} terkirim ke ${recipientEmails.length} orang`, 'success')
      for (const l of lampiran) if (l.previewUrl) URL.revokeObjectURL(l.previewUrl)
      setLampiran([])
      setTitle(''); setDesc(''); setVisibility('public'); setViewerEmails([]); setRecipientEmails([]); setPriority('medium'); reload()
    } finally { setBusy(false) }
  }

  return (
    <div className="grid md:grid-cols-[360px_1fr] gap-5 items-start">
      {/* Form ajukan */}
      <div className="bg-white border border-[#E8E7E3] rounded-2xl p-4">
        <h2 className="font-display text-sm font-bold text-[#1A1916] mb-3 flex items-center gap-2">
          <Send className="w-4 h-4" /> Ajukan Tiket
        </h2>
        <label className="block text-[11px] font-body font-semibold text-[#6B6860] mb-1">Judul</label>
        <input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={300}
          placeholder="Ringkas kebutuhanmu…"
          className="w-full mb-3 px-3 py-2 border border-[#E8E7E3] rounded-xl text-sm font-body focus:outline-none focus:border-[#1A1916]" />
        <label className="block text-[11px] font-body font-semibold text-[#6B6860] mb-1">Detail</label>
        <textarea value={desc} onChange={(e) => setDesc(e.target.value)} rows={5} maxLength={10000}
          placeholder="Jelaskan sedetail mungkin…"
          className="w-full mb-3 px-3 py-2 border border-[#E8E7E3] rounded-xl text-sm font-body resize-y focus:outline-none focus:border-[#1A1916]" />

        <label className="block text-[11px] font-body font-semibold text-[#6B6860] mb-1">Kirim ke</label>
        <div className="mb-3">
          <PeoplePicker value={recipientEmails} onChange={setRecipientEmails}
            exclude={myEmail ? [myEmail] : []} placeholder="Cari nama atau email" />
        </div>

        <div className="mb-3">
          <div className="flex items-center justify-between gap-2 mb-1">
            <label className="block text-[11px] font-body font-semibold text-[#6B6860]">Prioritas</label>
            <button type="button" onClick={() => setShowGuide((g) => !g)}
              className="text-[10px] font-body text-[#8A8A85] hover:text-[#1A1916] underline">
              {showGuide ? 'sembunyikan panduan' : 'apa bedanya?'}
            </button>
          </div>
          <div className="flex items-center gap-1 flex-wrap">
            {PRIORITIES.map((p) => (
              <button key={p} type="button" onClick={() => setPriority(p)} title={PRIO[p].guide}
                aria-pressed={priority === p}
                className={`px-2.5 py-1 rounded-lg text-[11px] font-body font-bold border transition-all min-h-[32px] ${
                  priority === p ? `${PRIO[p].cls} ring-1 ring-current`
                    : 'bg-white text-[#8A8A85] border-[#E8E7E3] hover:border-[#C8C7BF]'}`}>
                {PRIO[p].label}
              </button>
            ))}
          </div>
          {showGuide && <PriorityGuide />}
        </div>

        {/* Lampiran gambar */}
        <div className="mb-3">
          <label className="block text-[11px] font-body font-semibold text-[#6B6860] mb-1.5">
            Lampiran <span className="font-normal text-[#AEADA8]">gambar atau dokumen, maks {MAX_LAMPIRAN}</span>
          </label>
          {lampiran.length > 0 && (
            <div className="space-y-1.5 mb-2">
              <div className="flex flex-wrap gap-2">
                {lampiran.map((l, i) => l.previewUrl && (
                  <div key={l.file.name + i} className="relative w-16 h-16 rounded-lg overflow-hidden border border-[#E8E7E3] bg-[#F8F7F3]">
                    <img src={l.previewUrl} alt="" className="w-full h-full object-cover" />
                    <button type="button" onClick={() => buangLampiran(i)} aria-label="Hapus lampiran"
                      className="absolute top-0.5 right-0.5 w-5 h-5 rounded-full bg-white/90 border border-[#E8E7E3] flex items-center justify-center text-[#DC2626] hover:bg-white">
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
              {lampiran.map((l, i) => !l.previewUrl && (
                <div key={l.file.name + i} className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg border border-[#E8E7E3] bg-[#F8F7F3]">
                  <FileText className="w-3.5 h-3.5 text-[#8A8A85] flex-shrink-0" />
                  <span className="flex-1 min-w-0 truncate text-[11px] font-body text-[#1A1916]">{l.file.name}</span>
                  <span className="text-[10px] font-body text-[#AEADA8] flex-shrink-0">{formatKB(l.hasilBytes)}</span>
                  <button type="button" onClick={() => buangLampiran(i)} aria-label="Hapus lampiran"
                    className="w-5 h-5 rounded-full flex items-center justify-center text-[#DC2626] hover:bg-[#FEF2F2] flex-shrink-0">
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
          <input ref={fileRef} type="file" multiple className="hidden"
            accept={['image/*', ...DOC_EXTENSIONS.map((e) => `.${e}`)].join(',')}
            onChange={(e) => pilihGambar(e.target.files)} />
          <button type="button" onClick={() => fileRef.current?.click()}
            disabled={mengonversi || lampiran.length >= MAX_LAMPIRAN}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-xl border border-dashed border-[#C8C7BF] text-[12px] font-body text-[#6B6860] hover:border-[#8A8A85] disabled:opacity-50 min-h-[40px]">
            {mengonversi ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ImagePlus className="w-3.5 h-3.5" />}
            {mengonversi ? 'Memproses...' : 'Pilih gambar atau dokumen'}
          </button>
          {lampiran.some((l) => l.kind === 'image') && (
            <p className="mt-1.5 text-[10px] font-body text-[#AEADA8]">
              Gambar dikonversi ke WebP: {formatKB(lampiran.filter((l) => l.kind === 'image').reduce((a, l) => a + l.asalBytes, 0))} jadi {formatKB(lampiran.filter((l) => l.kind === 'image').reduce((a, l) => a + l.hasilBytes, 0))}
            </p>
          )}
        </div>

        <div className="mb-3">
          <VisibilityPicker value={visibility} onChange={setVisibility}
            viewerEmails={viewerEmails} onViewerEmailsChange={setViewerEmails} />
        </div>

        <button onClick={submit} disabled={busy}
          className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-[#1A1916] text-white text-sm font-medium font-body hover:bg-[#2E2E2A] disabled:opacity-50 min-h-[44px]">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Kirim Tiket
        </button>
      </div>

      {/* Ticket saya */}
      <div>
        <h2 className="font-display text-sm font-bold text-[#1A1916] mb-3 flex items-center gap-2">
          <Inbox className="w-4 h-4" /> Ticket Unit Saya <span className="text-[#C0BDB6] font-body font-normal">({tickets.length})</span>
        </h2>
        {tickets.length === 0 ? (
          <p className="text-sm text-[#AEADA8] font-body py-8 text-center bg-white border border-[#E8E7E3] rounded-2xl">Belum ada request.</p>
        ) : (
          <div className="space-y-2.5">
            {tickets.map((t) => (
              <div key={t.id} role="button" tabIndex={0} onClick={() => onOpen(t.id)}
                onKeyDown={(e) => { if (e.key === 'Enter') onOpen(t.id) }}
                title="Buka detail tiket"
                className="bg-white border border-[#E8E7E3] rounded-xl p-3.5 cursor-pointer hover:border-[#C8C7BF] transition-colors">
                <div className="flex items-start justify-between gap-2 mb-1">
                  <p className="font-body font-semibold text-[14px] text-[#1A1916] leading-snug">
                    <span className="mr-1.5 px-1.5 py-0.5 rounded-md bg-[#F4F3F0] text-[10px] font-bold tracking-wider text-[#6B6860] align-middle">{t.ticketKey}</span>
                    {t.title}
                  </p>
                  <div className="flex items-center gap-1.5 flex-shrink-0 flex-wrap justify-end">
                    <ConfidentialBadge v={t.visibility} /><PriorityInfo t={t} /><StatusBadge s={t.status} />
                  </div>
                </div>
                {t.description && <p className="text-[12px] text-[#8A8A85] font-body mb-2 line-clamp-2">{t.description}</p>}
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-[#8A8A85] font-body">
                  <span>Diajukan {formatDate(t.createdAt)}</span>
                  {t.queuePosition && (
                    <span className="font-semibold text-[#1A1916]" title="Urutan dikerjakan: prioritas → target selesai → tanggal masuk">
                      Antrean #{t.queuePosition} dari {t.queueTotal}
                    </span>
                  )}
                  {t.assigneeName && <span>PIC: <span className="font-semibold text-[#6B6860]">{t.assigneeName}</span></span>}
                  {t.dueDate && <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{t.dueDate}</span>}
                  {t.decidedByName && (
                    <span>{t.status === 'rejected' ? 'Ditolak' : 'Diproses'} oleh <span className="font-semibold text-[#6B6860]">{t.decidedByName}</span></span>
                  )}
                </div>
                {t.decisionNote && (
                  <p className={`mt-2 text-[11px] font-body flex items-start gap-1.5 ${t.status === 'rejected' ? 'text-[#DC2626]' : 'text-[#6B6860]'}`}>
                    <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                    {t.status === 'rejected' ? t.decisionNote : <span><span className="font-semibold">Catatan:</span> {t.decisionNote}</span>}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

// ─── View Holding ─────────────────────────────────────────────────────────────
function HoldingView({ data, reload, me, toast, onOpen }: {
  data: Payload; reload: () => void; me: string; toast: (m: string, t?: 'success' | 'error') => void
  onOpen: (id: string) => void
}) {
  const [triageOf, setTriageOf] = useState<Ticket | null>(null)
  const [prioBusy, setPrioBusy] = useState<string | null>(null)
  const workload = data.summary?.workload ?? []
  const perBU = data.summary?.perBU ?? {}
  const pendingTotal = data.summary?.pendingTotal ?? 0

  const pending = data.tickets.filter((t) => t.status === 'pending')
  const active = useMemo(() =>
    data.tickets.filter((t) => t.status === 'in_progress')
      .sort((a, b) => (PW[b.priority ?? ''] ?? 0) - (PW[a.priority ?? ''] ?? 0)
        || (a.dueDate ?? '9999').localeCompare(b.dueDate ?? '9999')),
    [data.tickets])
  const closed = data.tickets.filter((t) => t.status === 'done' || t.status === 'rejected')

  // Prioritas bisa diatur kapan saja — dipakai untuk menyusun antrean & terlihat pengaju.
  const setPriority = async (id: string, priority: Priority) => {
    setPrioBusy(id)
    try {
      const res = await fetch(`/api/ticketing/${id}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'priority', priority }),
      })
      const d = await res.json()
      if (!res.ok) { toast(d.error ?? 'Gagal mengatur prioritas', 'error'); return }
      toast(`Prioritas → ${PRIO[priority].label}`, 'success'); reload()
    } finally { setPrioBusy(null) }
  }

  const markDone = async (id: string) => {
    const res = await fetch(`/api/ticketing/${id}`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action: 'done' }),
    })
    const d = await res.json()
    if (!res.ok) { toast(d.error ?? 'Gagal', 'error'); return }
    toast('Ticket selesai', 'success'); reload()
  }

  const removeTicket = async (id: string) => {
    if (!window.confirm('Hapus tiket ini dari antrean? Tindakan ini tidak bisa dibatalkan.')) return
    const res = await fetch(`/api/ticketing/${id}`, { method: 'DELETE' })
    const d = await res.json().catch(() => ({}))
    if (!res.ok) { toast(d.error ?? 'Gagal menghapus', 'error'); return }
    toast('Tiket dihapus', 'success'); reload()
  }

  return (
    <div className="space-y-6">
      {/* Stat request masuk per BU */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        <div className="bg-[#1A1916] text-white rounded-2xl p-4">
          <p className="text-[11px] uppercase tracking-wider opacity-70 font-body">Menunggu Evaluasi</p>
          <p className="font-display text-3xl font-bold mt-1">{pendingTotal}</p>
        </div>
        {Object.keys(perBU).length === 0 ? (
          <div className="col-span-full sm:col-span-2 lg:col-span-4 flex items-center text-[13px] text-[#AEADA8] font-body px-2">
            Tidak ada request menunggu.
          </div>
        ) : Object.entries(perBU).sort((a, b) => b[1] - a[1]).map(([bu, n]) => {
          const dc = deptColor(bu)
          return (
            <div key={bu} className="bg-white border rounded-2xl p-4" style={{ borderColor: dc.border }}>
              <p className="text-[11px] font-body font-bold uppercase tracking-wider flex items-center gap-1.5" style={{ color: dc.text }}>
                <Building2 className="w-3.5 h-3.5" /> {bu}
              </p>
              <p className="font-display text-3xl font-bold mt-1 text-[#1A1916]">{n}</p>
              <p className="text-[10px] text-[#AEADA8] font-body">request baru</p>
            </div>
          )
        })}
      </div>

      {/* Beban PIC Holding */}
      <div className="bg-white border border-[#E8E7E3] rounded-2xl p-4">
        <h2 className="font-display text-sm font-bold text-[#1A1916] mb-3 flex items-center gap-2">
          <Users className="w-4 h-4" /> Beban PIC Holding
        </h2>
        {workload.length === 0 ? (
          <p className="text-[13px] text-[#AEADA8] font-body">Belum ada anggota Holding.</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {[...workload].sort((a, b) => a.openLoad - b.openLoad).map((m) => (
              <div key={m.email} className="flex items-center gap-2 px-3 py-1.5 rounded-xl border border-[#E8E7E3] bg-[#FAFAF8]">
                <span className="text-[12px] font-body font-semibold text-[#1A1916]">{m.name}</span>
                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${m.openLoad === 0 ? 'bg-[#DCFCE7] text-[#16A34A]' : m.openLoad >= 5 ? 'bg-[#FEE2E2] text-[#DC2626]' : 'bg-[#FEF3C7] text-[#B45309]'}`}>
                  {m.openLoad} aktif
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Antrean menunggu triase */}
      <Section title="Menunggu Evaluasi" count={pending.length} icon={<Inbox className="w-4 h-4" />}>
        {pending.length === 0 ? <Empty text="Tidak ada request menunggu." /> : pending.map((t) => (
          <div key={t.id} className="bg-white border border-[#E8E7E3] rounded-xl p-3.5">
            <div className="flex items-start justify-between gap-2 mb-1">
              <div className="min-w-0 cursor-pointer" role="button" tabIndex={0}
                onClick={() => onOpen(t.id)} onKeyDown={(e) => { if (e.key === 'Enter') onOpen(t.id) }}
                title="Buka detail tiket">
                <p className="font-body font-semibold text-[14px] text-[#1A1916] leading-snug flex items-center gap-1.5 flex-wrap">
                  <span className="px-1.5 py-0.5 rounded-md bg-[#F4F3F0] text-[10px] font-bold tracking-wider text-[#6B6860]">{t.ticketKey}</span>
                  {t.title} <ConfidentialBadge v={t.visibility} />
                </p>
                <p className="text-[11px] text-[#8A8A85] font-body mt-0.5">
                  dari <span className="font-semibold text-[#6B6860]">{t.requesterDepartment}</span> · {t.requesterName ?? t.requesterEmail} · {formatDate(t.createdAt)}
                </p>
              </div>
              <button onClick={() => setTriageOf(t)}
                className="flex-shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-xl bg-[#1A1916] text-white text-xs font-body font-medium hover:bg-[#2E2E2A] min-h-[36px]">
                Buka tiket <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </div>
            {t.description && <p className="text-[12px] text-[#8A8A85] font-body line-clamp-2">{t.description}</p>}
            {/* Prioritas bisa dikunci sebelum didelegasikan — pengaju langsung melihatnya. */}
            <div className="flex items-center gap-2 mt-2 pt-2 border-t border-[#F4F3F0] flex-wrap">
              <span className="text-[10px] uppercase tracking-wider text-[#AEADA8] font-body font-semibold">Prioritas</span>
              <PriorityPicker t={t} busy={prioBusy === t.id} onSet={(p) => setPriority(t.id, p)} />
            </div>
          </div>
        ))}
      </Section>

      {/* Sedang dikerjakan (antre by priority) */}
      <Section title="Dikerjakan (antrean)" count={active.length} icon={<Clock className="w-4 h-4" />}>
        {active.length === 0 ? <Empty text="Belum ada yang dikerjakan." /> : active.map((t) => (
          <div key={t.id} className="bg-white border border-[#E8E7E3] rounded-xl p-3.5 flex items-start justify-between gap-3">
            <div className="min-w-0 cursor-pointer" role="button" tabIndex={0}
              onClick={() => onOpen(t.id)} onKeyDown={(e) => { if (e.key === 'Enter') onOpen(t.id) }}
              title="Buka detail tiket">
              <div className="flex items-center gap-1.5 mb-1 flex-wrap">
                {t.queuePosition && (
                  <span className="text-[10px] px-2 py-0.5 rounded-full font-body font-bold bg-[#1A1916] text-white" title={`Antrean ${t.queuePosition} dari ${t.queueTotal}`}>
                    #{t.queuePosition}
                  </span>
                )}
                <PrioBadge p={t.priority} /><ConfidentialBadge v={t.visibility} />
                <span className="font-body font-semibold text-[14px] text-[#1A1916]">{t.title}</span>
              </div>
              <div className="mb-1"><PriorityPicker t={t} busy={prioBusy === t.id} onSet={(p) => setPriority(t.id, p)} /></div>
              <p className="text-[11px] text-[#8A8A85] font-body">
                dari <span className="font-semibold">{t.requesterDepartment}</span> · PIC <span className="font-semibold text-[#6B6860]">{t.assigneeName}</span>
                {t.dueDate && <> · <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" />{t.dueDate}</span></>}
              </p>
              {t.decisionNote && (
                <p className="text-[11px] text-[#6B6860] font-body mt-1 flex items-start gap-1.5">
                  <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" /> <span><span className="font-semibold">Catatan:</span> {t.decisionNote}</span>
                </p>
              )}
            </div>
            <div className="flex-shrink-0 flex items-center gap-2">
              <button onClick={() => markDone(t.id)} title="Tandai tiket selesai"
                className="relative flex items-center gap-1.5 px-3 py-2 rounded-xl bg-[#DCFCE7] text-[#16A34A] text-xs font-body font-semibold ring-1 ring-[#16A34A]/40 hover:bg-[#BBF7D0] min-h-[36px]">
                {/* Penanda: aksi menunggu diklik */}
                <span className="absolute -top-1 -right-1 flex h-2.5 w-2.5">
                  <span className="motion-safe:animate-ping absolute inline-flex h-full w-full rounded-full bg-[#16A34A] opacity-60" />
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#16A34A] ring-2 ring-white" />
                </span>
                <CheckCircle2 className="w-3.5 h-3.5" /> Selesai
              </button>
              <button onClick={() => removeTicket(t.id)} title="Hapus tiket" aria-label="Hapus tiket"
                className="flex items-center justify-center w-9 h-9 rounded-xl text-[#C0BDB6] hover:text-[#DC2626] hover:bg-[#FEF2F2] transition-colors">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
      </Section>

      {/* Riwayat */}
      {closed.length > 0 && (
        <Section title="Selesai / Ditolak" count={closed.length} icon={<Check className="w-4 h-4" />}>
          {closed.map((t) => (
            <div key={t.id} className="bg-white border border-[#EEEDE8] rounded-xl p-3 flex items-center justify-between gap-2">
              <div className="min-w-0">
                <p className="font-body text-[13px] text-[#3A3A36] truncate">{t.title}</p>
                <p className="text-[11px] text-[#AEADA8] font-body">{t.requesterDepartment} · {t.decidedByName ?? '—'}</p>
              </div>
              <div className="flex-shrink-0 flex items-center gap-2">
                <StatusBadge s={t.status} />
                <button onClick={() => removeTicket(t.id)} title="Hapus tiket" aria-label="Hapus tiket"
                  className="flex items-center justify-center w-9 h-9 rounded-xl text-[#C0BDB6] hover:text-[#DC2626] hover:bg-[#FEF2F2] transition-colors">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </Section>
      )}

      {triageOf && (
        <TriageModal ticket={triageOf} workload={workload} me={me}
          onClose={() => setTriageOf(null)} onDone={() => { setTriageOf(null); reload() }} toast={toast} />
      )}
    </div>
  )
}

interface TicketAttachment { id: string; url: string; contentType: string; bytes: number; filename: string | null; kind: 'image' | 'document'; uploadedByName: string | null; createdAt: string }

type MessageStatus = 'sent' | 'delivered' | 'read'
interface ChatMessage { id: string; authorEmail: string; authorName: string | null; body: string; createdAt: string; status?: MessageStatus }

/**
 * Tanda terima pesan sendiri: satu centang terkirim, dua centang diterima, dua
 * centang berwarna sudah dibaca.
 *
 * Hanya muncul di pesan SENDIRI. Di pesan orang lain, tanda terima itu tidak
 * menjawab pertanyaan siapa pun: yang membacanya ya kita sendiri.
 */
function StatusPesan({ status }: { status?: MessageStatus }) {
  if (!status) return null
  const judul = status === 'read' ? 'Dibaca' : status === 'delivered' ? 'Diterima' : 'Terkirim'
  if (status === 'sent') {
    return <Check className="w-3 h-3 flex-shrink-0 text-white/60" aria-label={judul} />
  }
  return (
    <CheckCheck
      className={`w-3 h-3 flex-shrink-0 ${status === 'read' ? 'text-[#7DA7FF]' : 'text-white/60'}`}
      aria-label={judul}
    />
  )
}

/**
 * Percakapan pengaju dengan penerima tiket.
 *
 * Dibaca berkala selama panel terbuka, bukan lewat SSE: kanal realtime yang ada
 * bergantung pada trigger NOTIFY di database, dan tabel ini tidak punya. Menambah
 * trigger demi obrolan berarti menaruh ketergantungan baru di tempat yang tidak
 * terlihat; menjadwal ulang tiap beberapa detik jauh lebih mudah ditebak.
 *
 * Tiket yang sudah selesai tetap bisa DIBACA, tidak bisa ditulis lagi. Percakapan
 * menempel pada pekerjaan yang sedang berjalan; setelah tutup, dia jadi arsip.
 */
function TicketChat({ ticketId, terbuka, myEmail, toast }: {
  ticketId: string
  terbuka: boolean
  myEmail: string
  toast: (m: string, t?: 'success' | 'error') => void
}) {
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [canWrite, setCanWrite] = useState(false)
  const [teks, setTeks] = useState('')
  const [kirim, setKirim] = useState(false)
  const bawahRef = useRef<HTMLDivElement>(null)

  const muat = useCallback(async () => {
    try {
      const res = await fetch(`/api/ticketing/${ticketId}/messages`)
      if (!res.ok) return
      const d = await res.json()
      setMessages(Array.isArray(d.messages) ? d.messages : [])
      setCanWrite(!!d.canWrite)
    } catch { /* diam: panel obrolan tidak boleh meributkan layar */ }
  }, [ticketId])

  useEffect(() => { muat() }, [muat])
  useEffect(() => {
    // Tiga pagar, semuanya soal tidak membuang kerja:
    //  - tiket selesai tidak akan pernah bertambah pesannya,
    //  - tab yang tidak terlihat tidak dibaca siapa pun, dan tiap penyegaran
    //    menulis tanda "dibaca" ke database, jadi tab yang ditinggalkan terbuka
    //    akan menulis selamanya,
    //  - 15 detik, bukan 5: percakapan tiket bukan obrolan waktu nyata, dan
    //    kolam koneksi database aplikasi ini cuma 5.
    if (!terbuka) return
    const jalan = () => { if (document.visibilityState === 'visible') muat() }
    const t = setInterval(jalan, 15_000)
    document.addEventListener('visibilitychange', jalan)
    return () => { clearInterval(t); document.removeEventListener('visibilitychange', jalan) }
  }, [muat, terbuka])
  useEffect(() => { bawahRef.current?.scrollIntoView({ block: 'nearest' }) }, [messages.length])

  const kirimPesan = async () => {
    const isi = teks.trim()
    if (!isi || kirim) return
    setKirim(true)
    try {
      const res = await fetch(`/api/ticketing/${ticketId}/messages`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ body: isi }),
      })
      const d = await res.json().catch(() => null)
      if (!res.ok) { toast(d?.error ?? 'Gagal mengirim pesan', 'error'); return }
      // Disaring per id: penyegaran berkala bisa mendarat tepat di antara POST dan
      // baris ini, dan pesan yang sama muncul dua kali dengan key React kembar.
      setMessages((prev) => prev.some((m) => m.id === d.message.id) ? prev : [...prev, d.message])
      setTeks('')
    } catch {
      // Jaringan putus atau server mati. Tanpa cabang ini, kegagalannya jatuh ke
      // promise tanpa penangkap: tombol berhenti berputar, pesan tetap di kotak,
      // dan tidak ada satu pun keterangan kenapa tidak terkirim.
      toast('Pesan gagal terkirim. Periksa koneksi lalu coba lagi.', 'error')
    } finally { setKirim(false) }
  }

  // Panelnya TIDAK disembunyikan saat kosong. Kolom yang kadang ada kadang tidak
  // membuat lebar isi tiket melompat-lompat tiap kali tiket dibuka.
  return (
    <div className="flex flex-col min-h-0 h-full">
      <p className="px-3 py-2.5 border-b border-[#F0EFEB] text-[11px] font-body font-semibold text-[#6B6860] flex-shrink-0">
        Percakapan {messages.length > 0 && <span className="font-normal text-[#A8A8A2]">({messages.length})</span>}
      </p>

      <div className="flex-1 min-h-0 overflow-auto space-y-2 p-3">
        {messages.length === 0 ? (
          <p className="text-[11px] font-body text-[#A8A8A2]">
            Belum ada percakapan. Tanyakan yang belum jelas di sini.
          </p>
        ) : messages.map((m) => {
          const saya = m.authorEmail.toLowerCase() === myEmail
          return (
            <div key={m.id} className={`flex ${saya ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[80%] px-3 py-2 rounded-xl ${
                saya ? 'bg-[#1A1916] text-white' : 'bg-[#F4F3F0] text-[#1A1916]'
              }`}>
                {!saya && (
                  <p className="text-[10px] font-body font-semibold text-[#6B6860] mb-0.5">
                    {m.authorName ?? m.authorEmail}
                  </p>
                )}
                <p className="text-[12px] font-body whitespace-pre-wrap break-words">{m.body}</p>
                <p className={`text-[10px] font-body mt-0.5 flex items-center gap-1 ${saya ? 'text-white/60 justify-end' : 'text-[#A8A8A2]'}`}>
                  {formatDate(m.createdAt)}
                  {saya && <StatusPesan status={m.status} />}
                </p>
              </div>
            </div>
          )
        })}
        <div ref={bawahRef} />
      </div>

      {canWrite ? (
        <div className="flex items-end gap-2 p-3 border-t border-[#F0EFEB] flex-shrink-0">
          <textarea
            value={teks}
            onChange={(e) => setTeks(e.target.value)}
            onKeyDown={(e) => {
              // Enter mengirim, Shift+Enter baris baru. Pola yang sudah jadi kebiasaan
              // orang di aplikasi obrolan mana pun.
              if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); kirimPesan() }
            }}
            rows={2}
            placeholder="Tulis pesan..."
            className="flex-1 px-3 py-2 bg-white border border-[#E0DFD8] rounded-xl text-[12px] font-body text-[#1A1916] placeholder:text-[#C0BDB6] focus:outline-none focus:border-[#C8C7BF] resize-none"
          />
          <button onClick={kirimPesan} disabled={kirim || !teks.trim()} aria-label="Kirim pesan"
            className="flex items-center justify-center w-10 h-10 rounded-xl bg-[#1A1916] text-white hover:bg-[#2E2E2A] disabled:opacity-40 flex-shrink-0">
            {kirim ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </button>
        </div>
      ) : (
        <p className="p-3 border-t border-[#F0EFEB] text-[11px] font-body text-[#A8A8A2] flex-shrink-0">
          {terbuka
            ? 'Hanya pengaju dan penerima tiket yang bisa mengirim pesan.'
            : 'Tiket sudah selesai, percakapan ditutup.'}
        </p>
      )}
    </div>
  )
}

function Section({ title, count, icon, children }: { title: string; count: number; icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <div>
      <h2 className="font-display text-sm font-bold text-[#1A1916] mb-2.5 flex items-center gap-2">
        {icon} {title} <span className="text-[#C0BDB6] font-body font-normal">({count})</span>
      </h2>
      <div className="space-y-2.5">{children}</div>
    </div>
  )
}
function Empty({ text }: { text: string }) {
  return <p className="text-sm text-[#AEADA8] font-body py-6 text-center bg-white border border-[#E8E7E3] rounded-2xl">{text}</p>
}

// ─── Modal triase (delegasi / tolak) ─────────────────────────────────────────
function TriageModal({ ticket, workload, onClose, onDone, toast }: {
  ticket: Ticket; workload: WorkloadMember[]; me: string
  onClose: () => void; onDone: () => void; toast: (m: string, t?: 'success' | 'error') => void
}) {
  const [assignee, setAssignee] = useState<string>('')
  const [priority, setPriority] = useState<string>('medium')
  const [dueDate, setDueDate] = useState<string>('')
  const [delegateNote, setDelegateNote] = useState('')
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState<'' | 'delegate' | 'reject'>('')

  const delegate = async () => {
    if (!assignee) { toast('Pilih assignee', 'error'); return }
    setBusy('delegate')
    try {
      const res = await fetch(`/api/ticketing/${ticket.id}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'delegate', assigneeEmail: assignee, priority, dueDate: dueDate || null, note: delegateNote.trim() || null }),
      })
      const d = await res.json()
      if (!res.ok) { toast(d.error ?? 'Gagal', 'error'); return }
      toast('Ticket didelegasikan', 'success'); onDone()
    } finally { setBusy('') }
  }
  const reject = async () => {
    setBusy('reject')
    try {
      const res = await fetch(`/api/ticketing/${ticket.id}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'reject', note: note.trim() }),
      })
      const d = await res.json()
      if (!res.ok) { toast(d.error ?? 'Gagal', 'error'); return }
      toast('Ticket ditolak', 'success'); onDone()
    } finally { setBusy('') }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-2 p-4 border-b border-[#F2F1EC]">
          <div className="min-w-0">
            <p className="font-display text-base font-bold text-[#1A1916] leading-snug">{ticket.title}</p>
            <p className="text-[11px] text-[#8A8A85] font-body mt-0.5">dari {ticket.requesterDepartment} · {ticket.requesterName ?? ticket.requesterEmail}</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg text-[#C0BDB6] hover:bg-[#F4F3F0]"><X className="w-4 h-4" /></button>
        </div>

        <div className="p-4 space-y-4">
          {ticket.description && (
            <p className="text-[13px] text-[#3A3A36] font-body whitespace-pre-wrap bg-[#FAFAF8] border border-[#EEEDE8] rounded-xl p-3">{ticket.description}</p>
          )}

          {/* Priority */}
          <div>
            <label className="block text-[11px] font-body font-semibold text-[#6B6860] mb-1.5">Priority</label>
            <div className="flex gap-2">
              {(['low', 'medium', 'high'] as const).map((p) => (
                <button key={p} onClick={() => setPriority(p)}
                  className={`flex-1 px-3 py-2 rounded-xl text-xs font-body font-bold border transition-all min-h-[36px] ${priority === p ? PRIO[p].cls + ' ring-1 ring-current' : 'bg-white text-[#8A8A85] border-[#E8E7E3]'}`}>
                  {PRIO[p].label}
                </button>
              ))}
            </div>
          </div>

          {/* Assignee (anggota Holding) + beban */}
          <div>
            <label className="block text-[11px] font-body font-semibold text-[#6B6860] mb-1.5">Delegasikan ke (anggota Holding)</label>
            <select value={assignee} onChange={(e) => setAssignee(e.target.value)}
              className="w-full px-3 py-2.5 border border-[#E8E7E3] rounded-xl text-sm font-body focus:outline-none focus:border-[#1A1916] min-h-[44px]">
              <option value="">— pilih PIC —</option>
              {[...workload].sort((a, b) => a.openLoad - b.openLoad).map((m) => (
                <option key={m.email} value={m.email}>{m.name} · {m.openLoad} aktif</option>
              ))}
            </select>
          </div>

          {/* Due date */}
          <div>
            <label className="block text-[11px] font-body font-semibold text-[#6B6860] mb-1.5">Target selesai</label>
            <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)}
              className="w-full px-3 py-2.5 border border-[#E8E7E3] rounded-xl text-sm font-body focus:outline-none focus:border-[#1A1916] min-h-[44px]" />
          </div>

          {/* Catatan delegasi — dilihat PIC & pengaju */}
          <div>
            <label className="block text-[11px] font-body font-semibold text-[#6B6860] mb-1.5">Catatan (untuk PIC &amp; pengaju)</label>
            <textarea value={delegateNote} onChange={(e) => setDelegateNote(e.target.value)} rows={3} maxLength={2000}
              placeholder="Arahan / konteks untuk PIC & pengaju…"
              className="w-full px-3 py-2 border border-[#E8E7E3] rounded-xl text-sm font-body resize-y focus:outline-none focus:border-[#1A1916]" />
          </div>

          <button onClick={delegate} disabled={busy !== ''}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-[#1A1916] text-white text-sm font-medium font-body hover:bg-[#2E2E2A] disabled:opacity-50 min-h-[44px]">
            {busy === 'delegate' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Delegasikan
          </button>

          {/* Tolak */}
          <div className="pt-3 border-t border-[#F2F1EC]">
            <label className="block text-[11px] font-body font-semibold text-[#6B6860] mb-1.5">Alasan tolak (opsional)</label>
            <div className="flex gap-2">
              <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Alasan…"
                className="flex-1 px-3 py-2 border border-[#E8E7E3] rounded-xl text-sm font-body focus:outline-none focus:border-[#DC2626]" />
              <button onClick={reject} disabled={busy !== ''}
                className="flex items-center gap-1.5 px-4 rounded-xl bg-[#FEE2E2] text-[#DC2626] text-sm font-body font-medium hover:bg-[#FECACA] disabled:opacity-50 min-h-[44px]">
                {busy === 'reject' ? <Loader2 className="w-4 h-4 animate-spin" /> : <X className="w-4 h-4" />} Tolak
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}


// ─── Kotak masuk: tiket yang DITUJUKAN ke saya ───────────────────────────────
// Semua penerima setara, jadi siapa pun boleh menerima, menolak, atau menyelesaikan.
// Menerima maupun menolak sama-sama membawa catatan yang dikirim balik ke pengirim:
// saat menerima untuk meminta data yang kurang, saat menolak untuk menjelaskan alasan.
function InboxView({ tickets, myEmail, reload, toast, onOpen }: {
  tickets: Ticket[]; myEmail: string | null; reload: () => void
  toast: (m: string, t?: 'success' | 'error') => void; onOpen: (id: string) => void
}) {
  const me = (myEmail ?? '').toLowerCase()
  const mine = useMemo(
    () => tickets.filter((t) => t.recipients?.some((r) => r.email.toLowerCase() === me)),
    [tickets, me],
  )
  const [openId, setOpenId] = useState<string | null>(null)
  const [mode, setMode] = useState<'accept' | 'reject'>('accept')
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)

  const respond = async (id: string) => {
    if (mode === 'reject' && !note.trim()) { toast('Alasan wajib diisi saat menolak', 'error'); return }
    setBusy(true)
    try {
      const res = await fetch(`/api/ticketing/${id}/respond`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: mode, note: note.trim() }),
      })
      const d = await res.json()
      if (!res.ok) { toast(d.error ?? 'Gagal mengirim jawaban', 'error'); return }
      toast(mode === 'accept' ? 'Tiket diterima, pengirim dikabari' : 'Tiket ditolak, alasan terkirim', 'success')
      setOpenId(null); setNote(''); reload()
    } finally { setBusy(false) }
  }

  const finish = async (id: string) => {
    const res = await fetch(`/api/ticketing/${id}`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'done' }),
    })
    const d = await res.json().catch(() => ({}))
    if (!res.ok) { toast(d.error ?? 'Gagal menyelesaikan', 'error'); return }
    toast('Tiket ditandai selesai', 'success'); reload()
  }

  if (mine.length === 0) {
    return (
      <div className="bg-white border border-[#E8E7E3] rounded-2xl p-12 text-center">
        <Inbox className="w-7 h-7 text-[#C8C7BF] mx-auto" />
        <p className="mt-3 text-sm font-body font-semibold text-[#1A1916]">Belum ada tiket untuk kamu</p>
        <p className="mt-1 text-xs font-body text-[#A8A8A2]">Tiket yang dikirim langsung ke kamu akan muncul di sini.</p>
      </div>
    )
  }

  return (
    <div className="space-y-2.5">
      {mine.map((t) => {
        const myRow = t.recipients.find((r) => r.email.toLowerCase() === me)
        const answered = !!myRow?.response
        const st = ST[t.status] ?? ST.pending
        return (
          <div key={t.id} className="bg-white border border-[#E8E7E3] rounded-xl p-4">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 cursor-pointer" role="button" tabIndex={0}
                onClick={() => onOpen(t.id)} onKeyDown={(e) => { if (e.key === 'Enter') onOpen(t.id) }}
                title="Buka detail tiket">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="px-1.5 py-0.5 rounded-md bg-[#F4F3F0] text-[10px] font-body font-bold tracking-wider text-[#6B6860]">{t.ticketKey}</span>
                  <PrioBadge p={t.priority} />
                  <span className={`px-2 py-0.5 rounded-md text-[10px] font-body font-bold ${st.cls}`}>{st.label}</span>
                  {myRow?.response && (
                    <span className={`px-2 py-0.5 rounded-md text-[10px] font-body font-bold ${myRow.response === 'accepted' ? 'bg-[#DCFCE7] text-[#16A34A]' : 'bg-[#FEE2E2] text-[#DC2626]'}`}>
                      {myRow.response === 'accepted' ? 'Kamu terima' : 'Kamu tolak'}
                    </span>
                  )}
                </div>
                <p className="mt-1.5 font-body font-semibold text-[14px] text-[#1A1916] leading-snug break-words">{t.title}</p>
                {t.description && <p className="mt-1 text-[13px] font-body text-[#6B6860] whitespace-pre-wrap break-words">{t.description}</p>}
                <p className="mt-1.5 text-[11px] font-body text-[#A8A8A2]">
                  dari {t.requesterName ?? t.requesterEmail} · {t.requesterDepartment} · {formatDate(t.createdAt)}
                </p>
                {t.recipients.length > 1 && (
                  <p className="mt-1 text-[11px] font-body text-[#A8A8A2]">
                    Penerima: {t.recipients.map((r) => r.name ?? r.email).join(', ')}
                  </p>
                )}
              </div>
            </div>

            {openId === t.id ? (
              <div className="mt-3 pt-3 border-t border-[#F0EFEB]">
                <div className="flex gap-1 mb-2">
                  {([['accept', 'Terima'], ['reject', 'Tolak']] as const).map(([k, l]) => (
                    <button key={k} onClick={() => setMode(k)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-body font-semibold border transition-colors ${mode === k ? 'bg-[#1A1916] text-white border-[#1A1916]' : 'bg-white text-[#6B6860] border-[#E8E7E3] hover:border-[#C8C7BF]'}`}>
                      {l}
                    </button>
                  ))}
                </div>
                <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} maxLength={4000}
                  placeholder={mode === 'accept' ? 'Catatan untuk pengirim, misal data apa yang masih kurang (opsional)' : 'Alasan menolak (wajib)'}
                  className="w-full px-3 py-2 border border-[#E8E7E3] rounded-xl text-sm font-body resize-y focus:outline-none focus:border-[#1A1916]" />
                <div className="flex gap-2 mt-2">
                  <button onClick={() => respond(t.id)} disabled={busy}
                    className="flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-xl bg-[#1A1916] text-white text-xs font-body font-semibold hover:bg-[#2E2E2A] disabled:opacity-50 min-h-[38px]">
                    {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Kirim jawaban
                  </button>
                  <button onClick={() => { setOpenId(null); setNote('') }}
                    className="px-3 py-2 rounded-xl border border-[#E8E7E3] text-xs font-body text-[#6B6860] hover:border-[#C8C7BF]">Batal</button>
                </div>
              </div>
            ) : (
              <div className="flex gap-2 mt-3">
                {!answered && t.status !== 'done' && t.status !== 'rejected' && (
                  <button onClick={() => { setOpenId(t.id); setMode('accept'); setNote('') }}
                    className="px-3 py-1.5 rounded-lg bg-[#1A1916] text-white text-xs font-body font-semibold hover:bg-[#2E2E2A]">
                    Terima / Tolak
                  </button>
                )}
                {t.status !== 'done' && t.status !== 'rejected' && (
                  <button onClick={() => finish(t.id)}
                    className="px-3 py-1.5 rounded-lg border border-[#E8E7E3] text-xs font-body font-semibold text-[#6B6860] hover:border-[#C8C7BF]">
                    Tandai selesai
                  </button>
                )}
              </div>
            )}

            {myRow?.responseNote && (
              <p className="mt-2 px-3 py-2 bg-[#F7F6F3] border border-[#E8E7E3] rounded-lg text-[12px] font-body text-[#4B4842] whitespace-pre-wrap">
                Catatanmu: {myRow.responseNote}
              </p>
            )}
          </div>
        )
      })}
    </div>
  )
}

// ─── Detail tiket ────────────────────────────────────────────────────────────
// Satu tempat untuk melihat seluruh isi tiket dan bertindak atasnya. Riwayatnya
// dibaca dari ticket_events di server, bukan disusun ulang dari kolom terakhir —
// `decision_note` cuma menyimpan satu catatan dan menimpa yang sebelumnya.
const EVENT_LABEL: Record<string, string> = {
  created: 'Tiket dibuat',
  accepted: 'Diterima',
  rejected: 'Ditolak',
  priority: 'Prioritas diubah',
  delegated: 'Didelegasikan',
  done: 'Ditandai selesai',
  project: 'Dijadikan project',
}

function TicketDetailModal({ id, myEmail, onClose, reload, toast }: {
  id: string
  myEmail: string | null
  onClose: () => void
  reload: () => void
  toast: (m: string, t?: 'success' | 'error') => void
}) {
  const [ticket, setTicket] = useState<(Ticket & { events?: TicketEvent[]; attachments?: TicketAttachment[] }) | null>(null)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState('')
  const [respondMode, setRespondMode] = useState<'' | 'accept' | 'reject'>('')
  const [note, setNote] = useState('')
  const [asProject, setAsProject] = useState(false)
  const [showGuide, setShowGuide] = useState(false)
  const me = (myEmail ?? '').toLowerCase()
  const { data: session } = useSession()
  // Cermin aturan server. Tombol yang tampil tapi ditolak server lebih buruk daripada
  // tombol yang tidak ada: pengguna menekan, dapat 403, dan mengira sistemnya rusak.
  const canDesk = canSeeAllInDepartment(session?.user?.role ?? null)

  // Panel penuh layar ini menggantikan sebuah modal, dan modal itu bisa ditutup
  // dengan Esc. Menghilangkan jalan keluar lewat keyboard demi tampilan yang lebih
  // lega adalah pertukaran yang buruk.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  const fetchOne = useCallback(async () => {
    try {
      const res = await fetch(`/api/ticketing/${id}`)
      const d = await res.json()
      if (!res.ok) { toast(d.error ?? 'Tiket tidak ditemukan', 'error'); onClose(); return }
      setTicket(d)
    } catch { toast('Gagal memuat tiket', 'error') } finally { setLoading(false) }
  }, [id, onClose, toast])
  useEffect(() => { fetchOne() }, [fetchOne])

  const myRow = ticket?.recipients?.find((r) => r.email.toLowerCase() === me)
  const isRecipient = !!myRow
  const isAssignee = (ticket?.assigneeEmail ?? '').toLowerCase() === me && !!me
  const isRequester = (ticket?.requesterEmail ?? '').toLowerCase() === me && !!me
  const involved = isRecipient || isAssignee || isRequester
  const open = ticket ? ticket.status !== 'done' && ticket.status !== 'rejected' : false
  // Server: prioritas & jadikan-project butuh keterlibatan atau pimpinan meja;
  // menandai selesai butuh penerima/PIC atau pimpinan meja (pengaju tidak termasuk).
  const bolehPrioritas = open && (involved || canDesk)
  const bolehProject = open && (involved || canDesk)
  const bolehSelesai = open && (isRecipient || isAssignee || canDesk)

  const setPriority = async (p: Priority) => {
    if (!ticket || busy) return
    setBusy('priority')
    try {
      const res = await fetch(`/api/ticketing/${ticket.id}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'priority', priority: p }),
      })
      const d = await res.json().catch(() => ({}))
      if (!res.ok) { toast(d.error ?? 'Gagal mengubah prioritas', 'error'); return }
      toast(`Prioritas jadi ${PRIO[p].label}`, 'success')
      await fetchOne(); reload()
    } finally { setBusy('') }
  }

  const respond = async () => {
    if (!ticket || !respondMode) return
    if (respondMode === 'reject' && !note.trim()) { toast('Alasan wajib diisi saat menolak', 'error'); return }
    setBusy('respond')
    try {
      const res = await fetch(`/api/ticketing/${ticket.id}/respond`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: respondMode, note: note.trim() }),
      })
      const d = await res.json().catch(() => ({}))
      if (!res.ok) { toast(d.error ?? 'Gagal mengirim jawaban', 'error'); return }
      toast(respondMode === 'accept' ? 'Tiket diterima, pengirim dikabari' : 'Tiket ditolak, alasan terkirim', 'success')
      setRespondMode(''); setNote(''); await fetchOne(); reload()
    } finally { setBusy('') }
  }

  const finish = async () => {
    if (!ticket) return
    setBusy('done')
    try {
      const res = await fetch(`/api/ticketing/${ticket.id}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'done' }),
      })
      const d = await res.json().catch(() => ({}))
      if (!res.ok) { toast(d.error ?? 'Gagal menyelesaikan', 'error'); return }
      toast('Tiket ditandai selesai', 'success'); await fetchOne(); reload()
    } finally { setBusy('') }
  }

  return (
    // Menempati seluruh layar KECUALI sidebar: left-14 menyisakan 56px lebar sidebar
    // yang menciut, dan z-30 menaruh panel ini DI BAWAH sidebar (z-40) supaya sidebar
    // masih bisa di-hover dan melebar di atasnya, bukan tertimbun.
    <>
    <div className="fixed inset-y-0 right-0 left-14 z-30 bg-white flex flex-col">
      {loading || !ticket ? (
        <div className="flex items-center justify-center gap-2 py-16 text-sm font-body text-[#8A8A85]">
          <Loader2 className="w-4 h-4 animate-spin" /> Memuat tiket…
        </div>
      ) : (
        <>
          <div className="flex items-start justify-between gap-3 p-4 border-b border-[#F2F1EC] flex-shrink-0">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1.5">
                  <span className="px-1.5 py-0.5 rounded-md bg-[#F4F3F0] text-[10px] font-body font-bold tracking-wider text-[#6B6860]">{ticket.ticketKey}</span>
                  <StatusBadge s={ticket.status} />
                  <PrioBadge p={ticket.priority} />
                  <ConfidentialBadge v={ticket.visibility} />
                </div>
                <p className="font-display text-base font-bold text-[#1A1916] leading-snug break-words">{ticket.title}</p>
                <p className="text-[11px] text-[#8A8A85] font-body mt-0.5">
                  dari {ticket.requesterName ?? ticket.requesterEmail} · {ticket.requesterDepartment} · {formatDate(ticket.createdAt)}
                </p>
              </div>
              <button onClick={onClose} aria-label="Tutup"
                className="p-1.5 rounded-lg text-[#C0BDB6] hover:bg-[#F4F3F0] flex-shrink-0"><X className="w-4 h-4" /></button>
            </div>

          {/* Isi tiket di kiri, percakapan di panel ramping kanan. min-h-0 wajib:
              tanpa itu anak fleks menolak mengecil dan kedua kolom ikut memanjang
              melewati layar, jadi yang bergulir halamannya, bukan kolomnya. */}
          <div className="flex-1 min-h-0 flex flex-col lg:flex-row">
            <div className="flex-1 min-h-0 overflow-auto p-4 space-y-4">
              {ticket.description && (
                <p className="text-[13px] text-[#3A3A36] font-body whitespace-pre-wrap break-words bg-[#FAFAF8] border border-[#EEEDE8] rounded-xl p-3">
                  {ticket.description}
                </p>
              )}

              {/* Prioritas — field manual, boleh dikoreksi selama tiket terbuka */}
              <div>
                <div className="flex items-center justify-between gap-2 mb-1.5">
                  <label className="block text-[11px] font-body font-semibold text-[#6B6860]">Prioritas</label>
                  <button type="button" onClick={() => setShowGuide((g) => !g)}
                    className="text-[10px] font-body text-[#8A8A85] hover:text-[#1A1916] underline">
                    {showGuide ? 'sembunyikan panduan' : 'apa bedanya?'}
                  </button>
                </div>
                {bolehPrioritas ? (
                  <PriorityPicker t={ticket} busy={busy === 'priority'} onSet={setPriority} />
                ) : (
                  <PriorityInfo t={ticket} />
                )}
                {showGuide && <PriorityGuide />}
              </div>

              {/* Penerima + jawabannya */}
              <div>
                <p className="text-[11px] font-body font-semibold text-[#6B6860] mb-1.5">
                  Penerima ({ticket.recipients.length})
                </p>
                <div className="space-y-1.5">
                  {ticket.recipients.map((r) => (
                    <div key={r.email} className="flex items-start justify-between gap-2 px-3 py-2 bg-[#FAFAF8] border border-[#EEEDE8] rounded-lg">
                      <div className="min-w-0">
                        <p className="text-[12px] font-body font-semibold text-[#1A1916] truncate">{r.name ?? r.email}</p>
                        {r.department && <p className="text-[10px] font-body text-[#A8A8A2]">{r.department}</p>}
                        {r.responseNote && (
                          <p className="mt-1 text-[11px] font-body text-[#4B4842] whitespace-pre-wrap break-words">{r.responseNote}</p>
                        )}
                      </div>
                      <span className={`px-2 py-0.5 rounded-md text-[10px] font-body font-bold flex-shrink-0 ${
                        r.response === 'accepted' ? 'bg-[#DCFCE7] text-[#16A34A]'
                          : r.response === 'rejected' ? 'bg-[#FEE2E2] text-[#DC2626]'
                          : 'bg-[#F4F3F0] text-[#8A8A85]'}`}>
                        {r.response === 'accepted' ? 'Terima' : r.response === 'rejected' ? 'Tolak' : 'Belum jawab'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Keterangan lain */}
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-[#8A8A85] font-body">
                {ticket.assigneeName && <span>PIC: <span className="font-semibold text-[#6B6860]">{ticket.assigneeName}</span></span>}
                {ticket.dueDate && <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{ticket.dueDate}</span>}
                {ticket.queuePosition && <span>Antrean #{ticket.queuePosition} dari {ticket.queueTotal}</span>}
              </div>

              {/* Project hasil tiket ini */}
              {ticket.projectId ? (
                <a href={`/projects/${ticket.projectId}`}
                  className="flex items-center gap-2 px-3 py-2.5 rounded-xl border border-[#E8E7E3] text-[12px] font-body font-semibold text-[#1A1916] hover:border-[#C8C7BF]">
                  <FolderPlus className="w-4 h-4" /> Buka project dari tiket ini
                </a>
              ) : bolehProject && (
                <button onClick={() => setAsProject(true)}
                  className="flex items-center gap-2 px-3 py-2.5 rounded-xl border border-[#E8E7E3] text-[12px] font-body font-semibold text-[#1A1916] hover:border-[#C8C7BF] w-full justify-center">
                  <FolderPlus className="w-4 h-4" /> Jadikan Project
                </button>
              )}

              {/* Aksi penerima */}
              {(bolehSelesai || (isRecipient && !myRow?.response)) && (
                <div className="pt-3 border-t border-[#F0EFEB]">
                  {respondMode ? (
                    <>
                      <div className="flex gap-1 mb-2">
                        {([['accept', 'Terima'], ['reject', 'Tolak']] as const).map(([k, l]) => (
                          <button key={k} onClick={() => setRespondMode(k)}
                            className={`px-3 py-1.5 rounded-lg text-xs font-body font-semibold border transition-colors ${respondMode === k ? 'bg-[#1A1916] text-white border-[#1A1916]' : 'bg-white text-[#6B6860] border-[#E8E7E3] hover:border-[#C8C7BF]'}`}>
                            {l}
                          </button>
                        ))}
                      </div>
                      <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} maxLength={4000}
                        placeholder={respondMode === 'accept' ? 'Catatan untuk pengirim, misal data apa yang masih kurang (opsional)' : 'Alasan menolak (wajib)'}
                        className="w-full px-3 py-2 border border-[#E8E7E3] rounded-xl text-sm font-body resize-y focus:outline-none focus:border-[#1A1916]" />
                      <div className="flex gap-2 mt-2">
                        <button onClick={respond} disabled={busy === 'respond'}
                          className="flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-xl bg-[#1A1916] text-white text-xs font-body font-semibold hover:bg-[#2E2E2A] disabled:opacity-50 min-h-[38px]">
                          {busy === 'respond' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Kirim jawaban
                        </button>
                        <button onClick={() => { setRespondMode(''); setNote('') }}
                          className="px-3 py-2 rounded-xl border border-[#E8E7E3] text-xs font-body text-[#6B6860] hover:border-[#C8C7BF]">Batal</button>
                      </div>
                    </>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {isRecipient && !myRow?.response && (
                        <button onClick={() => { setRespondMode('accept'); setNote('') }}
                          className="px-3 py-2 rounded-xl bg-[#1A1916] text-white text-xs font-body font-semibold hover:bg-[#2E2E2A]">
                          Terima / Tolak
                        </button>
                      )}
                      {bolehSelesai && (
                        <button onClick={finish} disabled={busy === 'done'}
                          className="px-3 py-2 rounded-xl border border-[#E8E7E3] text-xs font-body font-semibold text-[#6B6860] hover:border-[#C8C7BF] disabled:opacity-50">
                          {busy === 'done' ? 'Menyimpan…' : 'Tandai selesai'}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Lampiran */}
              {!!ticket.attachments?.length && (
                <div className="pt-3 border-t border-[#F0EFEB]">
                  <p className="text-[11px] font-body font-semibold text-[#6B6860] mb-2">
                    Lampiran <span className="font-normal text-[#A8A8A2]">({ticket.attachments.length})</span>
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {ticket.attachments.filter((a) => a.kind !== 'document').map((a) => (
                      <a key={a.id} href={a.url} target="_blank" rel="noreferrer"
                        title={`Dibuka di tab baru${a.uploadedByName ? ` · diunggah ${a.uploadedByName}` : ''}`}
                        className="block w-20 h-20 rounded-lg overflow-hidden border border-[#E8E7E3] bg-[#F8F7F3] hover:border-[#C8C7BF] transition-colors">
                        <img src={a.url} alt="" loading="lazy" className="w-full h-full object-cover" />
                      </a>
                    ))}
                  </div>
                  <div className="space-y-1.5 mt-2">
                    {ticket.attachments.filter((a) => a.kind === 'document').map((a) => (
                      // download memakai nama asli berkasnya, bukan UUID kunci objek.
                      <a key={a.id} href={a.url} download={a.filename ?? undefined} target="_blank" rel="noreferrer"
                        title={a.uploadedByName ? `Diunggah ${a.uploadedByName}` : undefined}
                        className="flex items-center gap-2 px-2.5 py-2 rounded-lg border border-[#E8E7E3] bg-[#F8F7F3] hover:border-[#C8C7BF] transition-colors">
                        <FileText className="w-3.5 h-3.5 text-[#8A8A85] flex-shrink-0" />
                        <span className="flex-1 min-w-0 truncate text-[12px] font-body text-[#1A1916]">
                          {a.filename ?? 'Dokumen'}
                        </span>
                        <span className="text-[10px] font-body text-[#AEADA8] flex-shrink-0">{formatKB(a.bytes)}</span>
                        <Download className="w-3.5 h-3.5 text-[#8A8A85] flex-shrink-0" />
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {/* Riwayat */}
              <div className="pt-3 border-t border-[#F0EFEB]">
                <p className="text-[11px] font-body font-semibold text-[#6B6860] mb-2">Riwayat</p>
                {!ticket.events?.length ? (
                  <p className="text-[11px] font-body text-[#A8A8A2]">
                    Belum ada jejak tercatat. Tiket lama dibuat sebelum riwayat ini ada.
                  </p>
                ) : (
                  <ol className="space-y-2">
                    {ticket.events.map((e) => (
                      <li key={e.id} className="flex items-start gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#C8C7BF] mt-1.5 flex-shrink-0" />
                        <div className="min-w-0">
                          <p className="text-[12px] font-body text-[#1A1916]">
                            <span className="font-semibold">{EVENT_LABEL[e.kind] ?? e.kind}</span>
                            {e.actorName ? ` oleh ${e.actorName}` : ''}
                            <span className="text-[#A8A8A2]"> · {formatDate(e.createdAt)}</span>
                          </p>
                          {e.note && <p className="text-[11px] font-body text-[#6B6860] whitespace-pre-wrap break-words">{e.note}</p>}
                        </div>
                      </li>
                    ))}
                  </ol>
                )}
              </div>
            </div>

            {/* Percakapan: panel ramping, tinggi penuh, gulirannya sendiri */}
            <aside className="flex flex-col min-h-0 border-t lg:border-t-0 lg:border-l border-[#F2F1EC] lg:w-[340px] lg:flex-shrink-0">
              <TicketChat ticketId={ticket.id} terbuka={open} myEmail={me} toast={toast} />
            </aside>
          </div>
        </>
      )}

    </div>

    {/* Di LUAR panel. `fixed` + z-index pada panel membuat konteks penumpukan
        sendiri, jadi modal yang dirender di dalamnya terkurung di bawah sidebar
        betapa pun tinggi z-index-nya. */}
    {asProject && ticket && (
      <AddAsProjectModal ticket={ticket} toast={toast}
        onClose={() => setAsProject(false)}
        onDone={async () => { setAsProject(false); await fetchOne(); reload() }} />
    )}
    </>
  )
}

// ─── Popup: jadikan tiket sebuah project ─────────────────────────────────────
// Pintasan, bukan otomatisasi. Judul tiket jarang layak jadi nama project apa adanya,
// dan project tanpa PIC atau tenggat cuma jadi kartu mati di papan — jadi isinya
// diminta di sini dulu, dengan tiket sebagai isian awal.
function AddAsProjectModal({ ticket, onClose, onDone, toast }: {
  ticket: Ticket
  onClose: () => void
  onDone: () => void
  toast: (m: string, t?: 'success' | 'error') => void
}) {
  const { users } = useRegisteredUsers()
  const [name, setName] = useState(ticket.title)
  const [description, setDescription] = useState(ticket.description ?? '')
  const [pic, setPic] = useState('')
  const [deadline, setDeadline] = useState(ticket.dueDate ?? '')
  const [visibility, setVisibility] = useState<Visibility>('public')
  const [viewerEmails, setViewerEmails] = useState<string[]>([])
  const [busy, setBusy] = useState(false)

  const submit = async () => {
    if (!name.trim()) { toast('Nama project wajib diisi', 'error'); return }
    if (visibilityNeedsViewers(visibility) && !viewerEmails.length) {
      toast('Pilih minimal satu orang yang boleh melihat', 'error'); return
    }
    setBusy(true)
    try {
      const res = await fetch(`/api/ticketing/${ticket.id}/project`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name.trim(), description: description.trim(), pic: pic || null,
          deadline: deadline || null, visibility, viewerEmails,
        }),
      })
      const d = await res.json().catch(() => ({}))
      if (!res.ok) { toast(d.error ?? 'Gagal membuat project', 'error'); return }
      toast(
        d.approvalStatus === 'pending'
          ? 'Project dibuat, menunggu persetujuan atasan'
          : 'Project dibuat dari tiket ini',
        'success',
      )
      onDone()
    } finally { setBusy(false) }
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/40" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between gap-2 p-4 border-b border-[#F2F1EC]">
          <div>
            <p className="font-display text-base font-bold text-[#1A1916]">Jadikan Project</p>
            <p className="text-[11px] text-[#8A8A85] font-body mt-0.5">Dari tiket {ticket.ticketKey}. Rapikan dulu sebelum disimpan.</p>
          </div>
          <button onClick={onClose} aria-label="Tutup"
            className="p-1.5 rounded-lg text-[#C0BDB6] hover:bg-[#F4F3F0]"><X className="w-4 h-4" /></button>
        </div>

        <div className="p-4 space-y-3">
          <div>
            <label className="block text-[11px] font-body font-semibold text-[#6B6860] mb-1">Nama project</label>
            <input value={name} onChange={(e) => setName(e.target.value)} maxLength={200}
              className="w-full px-3 py-2 border border-[#E8E7E3] rounded-xl text-sm font-body focus:outline-none focus:border-[#1A1916]" />
          </div>
          <div>
            <label className="block text-[11px] font-body font-semibold text-[#6B6860] mb-1">Deskripsi</label>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={4} maxLength={5000}
              className="w-full px-3 py-2 border border-[#E8E7E3] rounded-xl text-sm font-body resize-y focus:outline-none focus:border-[#1A1916]" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[11px] font-body font-semibold text-[#6B6860] mb-1">PIC</label>
              <select value={pic} onChange={(e) => setPic(e.target.value)}
                className="w-full px-3 py-2.5 border border-[#E8E7E3] rounded-xl text-sm font-body focus:outline-none focus:border-[#1A1916] min-h-[42px]">
                <option value="">— belum ditentukan —</option>
                {users.map((u) => <option key={u.email} value={u.username}>{u.username}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[11px] font-body font-semibold text-[#6B6860] mb-1">Deadline</label>
              <input type="date" value={deadline} onChange={(e) => setDeadline(e.target.value)}
                className="w-full px-3 py-2 border border-[#E8E7E3] rounded-xl text-sm font-body focus:outline-none focus:border-[#1A1916] min-h-[42px]" />
            </div>
          </div>
          <VisibilityPicker value={visibility} onChange={setVisibility}
            viewerEmails={viewerEmails} onViewerEmailsChange={setViewerEmails} />
          <p className="text-[11px] font-body text-[#A8A8A2]">
            Menunjuk PIC di luar wewenangmu membuat project menunggu persetujuan atasan, sama seperti membuatnya dari halaman Projects.
          </p>
        </div>

        <div className="flex items-center justify-end gap-2 p-4 border-t border-[#F2F1EC]">
          <button onClick={onClose} className="px-4 py-2 text-sm font-body text-[#8A8A85] hover:text-[#1A1916]">Batal</button>
          <button onClick={submit} disabled={busy}
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-[#1A1916] text-white text-sm font-body font-medium hover:bg-[#2E2E2A] disabled:opacity-50 min-h-[40px]">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <FolderPlus className="w-4 h-4" />} Buat project
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Papan progress (kanban) ─────────────────────────────────────────────────
// Kolom mengikuti status tiket. Kartu bisa diseret HANYA untuk perpindahan yang tidak
// butuh isian tambahan: menerima tiket dan menandainya selesai. Menolak butuh alasan,
// jadi seretan ke sana membuka detail — lebih baik daripada diam-diam menolak tanpa
// alasan, yang justru merusak balasan ke pengirim.
const BOARD_COLUMNS: { key: Ticket['status']; label: string }[] = [
  { key: 'pending', label: 'Menunggu' },
  { key: 'in_progress', label: 'Dikerjakan' },
  { key: 'done', label: 'Selesai' },
  { key: 'rejected', label: 'Ditolak' },
]

export function BoardView({ tickets, myEmail, reload, toast, onOpen }: {
  tickets: Ticket[]; myEmail: string | null; reload: () => void
  toast: (m: string, t?: 'success' | 'error') => void; onOpen: (id: string) => void
}) {
  const me = (myEmail ?? '').toLowerCase()
  const [scope, setScope] = useState<'all' | 'mine'>('all')
  const [dragId, setDragId] = useState<string | null>(null)
  const [overCol, setOverCol] = useState<Ticket['status'] | null>(null)
  const [busy, setBusy] = useState(false)

  const involved = useCallback(
    (t: Ticket) =>
      t.requesterEmail.toLowerCase() === me
      || (t.assigneeEmail ?? '').toLowerCase() === me
      || t.recipients?.some((r) => r.email.toLowerCase() === me),
    [me],
  )
  const shown = useMemo(
    () => (scope === 'mine' ? tickets.filter(involved) : tickets),
    [tickets, scope, involved],
  )
  const byCol = useMemo(() => {
    const m = new Map<Ticket['status'], Ticket[]>()
    for (const col of BOARD_COLUMNS) m.set(col.key, [])
    for (const t of shown) m.get(t.status)?.push(t)
    for (const [, list] of m) {
      list.sort((a, b) => (PW[b.priority ?? ''] ?? 0) - (PW[a.priority ?? ''] ?? 0)
        || (a.dueDate ?? '9999').localeCompare(b.dueDate ?? '9999'))
    }
    return m
  }, [shown])

  const drop = async (to: Ticket['status']) => {
    const id = dragId
    setDragId(null); setOverCol(null)
    const t = id ? tickets.find((x) => x.id === id) : null
    if (!t || t.status === to || busy) return
    if (t.status === 'done' || t.status === 'rejected') { toast('Tiket sudah ditutup.', 'error'); return }
    if (to === 'pending') { toast('Tiket tidak bisa dikembalikan ke Menunggu.', 'error'); return }
    if (to === 'rejected') { toast('Menolak butuh alasan — buka detail tiketnya.', 'error'); onOpen(t.id); return }

    const myRow = t.recipients?.find((r) => r.email.toLowerCase() === me)
    setBusy(true)
    try {
      if (to === 'in_progress') {
        if (!myRow || myRow.response) { toast('Hanya penerima yang belum menjawab yang bisa menerima tiket ini.', 'error'); onOpen(t.id); return }
        const res = await fetch(`/api/ticketing/${t.id}/respond`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'accept', note: '' }),
        })
        const d = await res.json().catch(() => ({}))
        if (!res.ok) { toast(d.error ?? 'Gagal menerima tiket', 'error'); return }
        toast('Tiket diterima, pengirim dikabari', 'success')
      } else {
        const res = await fetch(`/api/ticketing/${t.id}`, {
          method: 'PATCH', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'done' }),
        })
        const d = await res.json().catch(() => ({}))
        if (!res.ok) { toast(d.error ?? 'Gagal menyelesaikan', 'error'); return }
        toast('Tiket ditandai selesai', 'success')
      }
      reload()
    } finally { setBusy(false) }
  }

  return (
    <div>
      <div className="flex items-center gap-1 mb-4">
        {([['all', 'Semua tiket'], ['mine', 'Yang melibatkan saya']] as const).map(([k, l]) => (
          <button key={k} onClick={() => setScope(k)}
            className={`px-3 py-1.5 rounded-xl text-xs font-body font-semibold border transition-colors ${
              scope === k ? 'bg-[#1A1916] text-white border-[#1A1916]' : 'bg-white text-[#6B6860] border-[#E8E7E3] hover:border-[#C8C7BF]'}`}>
            {l}
          </button>
        ))}
      </div>

      <div className="grid gap-3 md:grid-cols-4">
        {BOARD_COLUMNS.map((col) => {
          const list = byCol.get(col.key) ?? []
          return (
            <div key={col.key}
              onDragOver={(e) => { e.preventDefault(); setOverCol(col.key) }}
              onDragLeave={() => setOverCol((c) => (c === col.key ? null : c))}
              onDrop={(e) => { e.preventDefault(); drop(col.key) }}
              className={`rounded-2xl border p-2.5 min-h-[180px] transition-colors ${
                overCol === col.key ? 'border-[#1A1916] bg-[#FAFAF8]' : 'border-[#E8E7E3] bg-white'}`}>
              <div className="flex items-center justify-between px-1 pb-2">
                <span className="text-[11px] font-body font-bold uppercase tracking-wider text-[#6B6860]">{col.label}</span>
                <span className="text-[11px] font-body text-[#A8A8A2]">{list.length}</span>
              </div>
              <div className="space-y-2">
                {list.length === 0 && (
                  <p className="text-[11px] font-body text-[#C0BDB6] px-1 py-3 text-center">Kosong</p>
                )}
                {list.map((t) => (
                  <div key={t.id} draggable={t.status !== 'done' && t.status !== 'rejected'}
                    onDragStart={() => setDragId(t.id)} onDragEnd={() => { setDragId(null); setOverCol(null) }}
                    role="button" tabIndex={0}
                    onClick={() => onOpen(t.id)} onKeyDown={(e) => { if (e.key === 'Enter') onOpen(t.id) }}
                    title="Klik untuk detail, seret untuk memindahkan"
                    className={`bg-white border border-[#E8E7E3] rounded-xl p-2.5 cursor-pointer hover:border-[#C8C7BF] transition-all ${
                      dragId === t.id ? 'opacity-40' : ''}`}>
                    <div className="flex items-center gap-1.5 flex-wrap mb-1">
                      <span className="px-1.5 py-0.5 rounded bg-[#F4F3F0] text-[9px] font-body font-bold tracking-wider text-[#6B6860]">{t.ticketKey}</span>
                      <PrioBadge p={t.priority} />
                      {t.projectId && (
                        <span className="px-1.5 py-0.5 rounded bg-[#F4F3F0] text-[9px] font-body font-bold text-[#6B6860]">project</span>
                      )}
                    </div>
                    <p className="text-[12px] font-body font-semibold text-[#1A1916] leading-snug break-words">{t.title}</p>
                    <p className="mt-1 text-[10px] font-body text-[#A8A8A2] truncate">
                      {t.requesterName ?? t.requesterEmail} · {formatDate(t.createdAt)}
                    </p>
                    {t.dueDate && (
                      <p className="mt-0.5 text-[10px] font-body text-[#8A8A85] flex items-center gap-1">
                        <Clock className="w-2.5 h-2.5" />{t.dueDate}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )
        })}
      </div>
      <p className="mt-3 text-[11px] font-body text-[#A8A8A2]">
        Seret kartu ke Dikerjakan untuk menerima tiket, atau ke Selesai untuk menutupnya. Menolak butuh alasan, jadi dilakukan dari detail tiket.
      </p>
    </div>
  )
}

// ─── Riwayat tiket ───────────────────────────────────────────────────────────
// Dua arah yang berbeda dan sering tertukar: tiket yang SAYA kirim, dan tiket yang
// dikirim KE saya. Filternya memisahkan keduanya, bukan menggabung jadi satu daftar.
export function HistoryView({ tickets, myEmail, onOpen }: {
  tickets: Ticket[]; myEmail: string | null; onOpen: (id: string) => void
}) {
  const me = (myEmail ?? '').toLowerCase()
  const [dir, setDir] = useState<'all' | 'sent' | 'received'>('all')
  const [openOnly, setOpenOnly] = useState(false)
  const [cari, setCari] = useState('')

  const rows = useMemo(() => {
    const sent = (t: Ticket) => t.requesterEmail.toLowerCase() === me
    const received = (t: Ticket) =>
      t.recipients?.some((r) => r.email.toLowerCase() === me) || (t.assigneeEmail ?? '').toLowerCase() === me
    const q = cari.trim().toLowerCase()
    return tickets
      .filter((t) => (dir === 'sent' ? sent(t) : dir === 'received' ? received(t) : sent(t) || received(t)))
      .filter((t) => (openOnly ? t.status !== 'done' && t.status !== 'rejected' : true))
      .filter((t) => !q || t.title.toLowerCase().includes(q) || t.ticketKey.toLowerCase().includes(q))
      .slice()
      .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))
  }, [tickets, me, dir, openOnly, cari])

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2 mb-4">
        {([['all', 'Semua'], ['sent', 'Dikirim'], ['received', 'Diterima']] as const).map(([k, l]) => (
          <button key={k} onClick={() => setDir(k)}
            className={`px-3 py-1.5 rounded-xl text-xs font-body font-semibold border transition-colors ${
              dir === k ? 'bg-[#1A1916] text-white border-[#1A1916]' : 'bg-white text-[#6B6860] border-[#E8E7E3] hover:border-[#C8C7BF]'}`}>
            {l}
          </button>
        ))}
        <button onClick={() => setOpenOnly((v) => !v)}
          className={`px-3 py-1.5 rounded-xl text-xs font-body font-semibold border transition-colors ${
            openOnly ? 'bg-[#1A1916] text-white border-[#1A1916]' : 'bg-white text-[#6B6860] border-[#E8E7E3] hover:border-[#C8C7BF]'}`}>
          Belum ditutup
        </button>
        <input value={cari} onChange={(e) => setCari(e.target.value)} placeholder="Cari judul atau TKT-…"
          className="ml-auto px-3 py-1.5 border border-[#E8E7E3] rounded-xl text-xs font-body focus:outline-none focus:border-[#1A1916] min-w-[180px]" />
      </div>

      {rows.length === 0 ? (
        <Empty text="Tidak ada tiket pada filter ini." />
      ) : (
        <div className="space-y-2">
          {rows.map((t) => {
            const sent = t.requesterEmail.toLowerCase() === me
            const lawan = sent
              ? (t.recipients?.map((r) => r.name ?? r.email).join(', ') || '—')
              : (t.requesterName ?? t.requesterEmail)
            return (
              <div key={t.id} role="button" tabIndex={0}
                onClick={() => onOpen(t.id)} onKeyDown={(e) => { if (e.key === 'Enter') onOpen(t.id) }}
                title="Buka detail tiket"
                className="bg-white border border-[#E8E7E3] rounded-xl p-3 flex items-start justify-between gap-3 cursor-pointer hover:border-[#C8C7BF] transition-colors">
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5 flex-wrap mb-1">
                    <span className="px-1.5 py-0.5 rounded bg-[#F4F3F0] text-[9px] font-body font-bold tracking-wider text-[#6B6860]">{t.ticketKey}</span>
                    <span className={`px-2 py-0.5 rounded-md text-[10px] font-body font-bold ${sent ? 'bg-[#EEF2FF] text-[#3730A3]' : 'bg-[#F0FDF4] text-[#15803D]'}`}>
                      {sent ? 'Dikirim' : 'Diterima'}
                    </span>
                    <StatusBadge s={t.status} />
                    <PrioBadge p={t.priority} />
                  </div>
                  <p className="text-[13px] font-body font-semibold text-[#1A1916] leading-snug break-words">{t.title}</p>
                  <p className="mt-0.5 text-[11px] font-body text-[#A8A8A2] truncate">
                    {sent ? 'ke' : 'dari'} {lawan} · diperbarui {formatDate(t.updatedAt)}
                  </p>
                </div>
                <ArrowRight className="w-4 h-4 text-[#C0BDB6] flex-shrink-0 mt-1" />
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
